// Browser fingerprint used to dedupe anonymous ratings per profile.
//
// Generates a random opaque id on first use, persists it to
// localStorage, and re-uses it forever after. Resets if the user clears
// site data (acceptable — they can re-rate). NOT a real fingerprint
// in the privacy-invasive sense; just a stable per-browser token the
// backend dedupes against. Anti-brigading lives in IP rate-limits +
// future hCaptcha, not here.

const KEY = 'wydm.rater_fingerprint';

export function getOrCreateFingerprint(): string {
  try {
    const existing = localStorage.getItem(KEY);
    if (existing && existing.length >= 8) return existing;
    const fresh = generateFingerprint();
    localStorage.setItem(KEY, fresh);
    return fresh;
  } catch {
    // localStorage disabled (incognito / iOS Safari quirk) — fall back
    // to a session-scoped value via crypto.randomUUID. Each tab gets
    // its own; defeats dedupe but lets the rate flow at least work.
    return generateFingerprint();
  }
}

function generateFingerprint(): string {
  // crypto.randomUUID is widely supported (Chrome 92+, Safari 15.4+,
  // Firefox 95+). Falls back to a Math.random hex string for older
  // browsers — weaker collision resistance but the dedupe is
  // best-effort anyway.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // 16 random hex chars (~64 bits of entropy) — fine for dedupe.
  const buf = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(buf);
  } else {
    for (let i = 0; i < buf.length; i++) buf[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('');
}
